/**
 * Card de credenciais WABA (WhatsApp Business API)
 * Exibe status das credenciais de forma segura (sem expor tokens)
 * 
 * @version 1.0.0
 * @date 2026-01-16
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Key, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Calendar,
  Building2,
  Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { WabaCredentialsSafe } from '@/hooks/useInboxDetails';

// ============================================================================
// INTERFACES
// ============================================================================

interface WabaCredentialsCardProps {
  credentials: WabaCredentialsSafe | null;
  isLoading: boolean;
  onReconnect?: () => void;
}

// ============================================================================
// COMPONENTE AUXILIAR: LINHA DE CREDENCIAL
// ============================================================================

interface CredentialRowProps {
  label: string;
  value: string | null;
  isConfigured: boolean;
  masked?: boolean;
}

function CredentialRow({ label, value, isConfigured, masked = false }: CredentialRowProps) {
  const displayValue = masked 
    ? '••••••••••••••••' 
    : (value || '-');
  
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className={cn(
          "text-sm font-mono",
          !isConfigured && "text-muted-foreground"
        )}>
          {displayValue}
        </span>
        {isConfigured ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-destructive" />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function WabaCredentialsCard({ 
  credentials, 
  isLoading,
  onReconnect 
}: WabaCredentialsCardProps) {
  const { t } = useTranslation('pages');

  // Se está carregando, mostrar skeleton
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Key className="h-4 w-4 text-primary" />
            {t('inboxDetails.wabaCredentials', 'Credenciais WhatsApp Business API')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Verificar se tem credenciais configuradas
  const hasCredentials = credentials && (
    credentials.has_access_token || 
    credentials.waba_id || 
    credentials.phone_number_id
  );

  // Calcular dias restantes do token
  const tokenExpiresAt = credentials?.token_expires_at 
    ? new Date(credentials.token_expires_at) 
    : null;
  const daysUntilExpiry = tokenExpiresAt 
    ? differenceInDays(tokenExpiresAt, new Date()) 
    : null;
  const isTokenExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const isTokenExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Key className="h-4 w-4 text-primary" />
          {t('inboxDetails.wabaCredentials', 'Credenciais WhatsApp Business API')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Alerta se não tem credenciais */}
        {!hasCredentials && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                {t('inboxDetails.noCredentials', 'Credenciais não configuradas. Reconecte para configurar.')}
              </span>
              {onReconnect && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onReconnect}
                  className="ml-4"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('inboxDetails.reconnect', 'Reconectar')}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Alerta se token está expirando */}
        {hasCredentials && isTokenExpiringSoon && !isTokenExpired && (
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-600 dark:text-amber-400">
              {t('inboxDetails.tokenExpiringSoon', 'Token expira em {{days}} dias. Reconecte para renovar.', { 
                days: daysUntilExpiry 
              })}
            </AlertDescription>
          </Alert>
        )}

        {/* Alerta se token expirou */}
        {hasCredentials && isTokenExpired && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                {t('inboxDetails.tokenExpired', 'Token expirado. Reconecte para renovar.')}
              </span>
              {onReconnect && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onReconnect}
                  className="ml-4"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('inboxDetails.reconnect', 'Reconectar')}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Lista de credenciais */}
        {hasCredentials && credentials && (
          <div className="divide-y">
            <CredentialRow 
              label="WABA ID"
              value={credentials.waba_id}
              isConfigured={!!credentials.waba_id}
            />
            
            <CredentialRow 
              label="Phone Number ID"
              value={credentials.phone_number_id}
              isConfigured={!!credentials.phone_number_id}
            />
            
            <CredentialRow 
              label="Access Token"
              value={null}
              isConfigured={credentials.has_access_token}
              masked={true}
            />
            
            <CredentialRow 
              label="Webhook Token"
              value={null}
              isConfigured={credentials.has_webhook_verify_token}
              masked={true}
            />

            {/* Expiração do Token */}
            {tokenExpiresAt && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('inboxDetails.expiresAt', 'Expira em')}
                </span>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-sm",
                    isTokenExpired && "text-destructive",
                    isTokenExpiringSoon && !isTokenExpired && "text-amber-600 dark:text-amber-400"
                  )}>
                    {format(tokenExpiresAt, "dd/MM/yyyy", { locale: ptBR })}
                    {!isTokenExpired && daysUntilExpiry !== null && (
                      <span className="text-muted-foreground ml-1">
                        ({daysUntilExpiry} {t('inboxDetails.daysRemaining', 'dias restantes')})
                      </span>
                    )}
                  </span>
                  {isTokenExpired ? (
                    <Badge variant="destructive" className="text-[10px]">
                      {t('inboxDetails.expired', 'Expirado')}
                    </Badge>
                  ) : isTokenExpiringSoon ? (
                    <Badge className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20">
                      {t('inboxDetails.expiringSoon', 'Expirando')}
                    </Badge>
                  ) : (
                    <Badge className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">
                      {t('inboxDetails.valid', 'Válido')}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Nome da Empresa */}
            {credentials.business_name && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {t('inboxDetails.businessName', 'Empresa')}
                </span>
                <span className="text-sm font-medium">{credentials.business_name}</span>
              </div>
            )}

            {/* Número de exibição */}
            {credentials.display_phone_number && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">
                  {t('inboxDetails.displayPhone', 'Número')}
                </span>
                <span className="text-sm font-mono">{credentials.display_phone_number}</span>
              </div>
            )}

            {/* Última validação */}
            {credentials.last_validated_at && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  {t('inboxDetails.lastValidated', 'Última validação')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(credentials.last_validated_at), { 
                    addSuffix: true, 
                    locale: ptBR 
                  })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Botão de reconexão se tem credenciais mas quer atualizar */}
        {hasCredentials && onReconnect && !isTokenExpired && !isTokenExpiringSoon && (
          <div className="pt-2 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onReconnect}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('inboxDetails.updateCredentials', 'Atualizar Credenciais')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
